import {createAction, ActionCreator} from '@packages/utilities/create-action';
import {Component, Plugins, type ExpressionPath} from '@packages/systems/wfdl';
import {type ExpressionEditorActionPayload} from '@packages/systems/ExpressionEditor-utils';
import {type StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {RelativePositionPlacementOptions} from '@packages/utilities/platform';
import {type DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {type NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';

import {
  ELEMENT_ADDED,
  IMAGE_PREVIEW_UPDATED,
  ELEMENT_MOVED,
  ROOT_SYMBOL_NODE_REMOVED,
  ROOT_SYMBOL_NODE_RENAMED,
  ROOT_SYMBOL_NODE_VISIBILITY_CHANGED,
  __HACK__CLEARFIX_STATE_CHANGED,
  PAGE_NODE_DYN_BINDING_REMOVED,
  SYMBOL_NODE_DYN_BINDING_REMOVED,
  DATA_TYPE_CHANGED,
  SELECTED_NODE_REMOVED,
  DYNAMO_BINDING_REMOVED,
} from '@packages/systems/designer/actions';

import type {RawFileType} from '@packages/systems/core/records/FileRecord';
import {NodeBindingType} from '@packages/systems/dynamo/utils/DynamoElementUtils';

export type ElementMovedPayload = {
  id: string;
  anchorId: string;
  anchorPath?: ExpressionPath | null;
  position: RelativePositionPlacementOptions;
  sourceNativeIdPath?: NativeId[] | null;
  anchorNativeIdPath?: NativeId[] | null;
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  styleBlockState: StyleBlockStateType;
  from?: 'shortcut' | 'drag';
};

export type ElementAddedPayload = {
  anchorId: string | null;
  anchorPath?: ExpressionPath | null;
  anchorNativeIdPath?: NativeId[] | null;
  position: RelativePositionPlacementOptions;
  nativeId: NativeId;
  elementPreset: Plugins.QualifiedElementPreset | null;
  initialStyleBlockId: string;
  styleBlockState: StyleBlockStateType;
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  element: ExpressionElement;
  idMap?: {
    [key: string]: string;
  };
  assetsToImport?: Array<RawFileType>;
  componentMapPatch?: Component.ComponentMap | null;
  targetComponentId?: string | null;
};

export const elementAdded: ActionCreator<
  typeof ELEMENT_ADDED,
  ElementAddedPayload
> = createAction(ELEMENT_ADDED);

export const elementMoved: ActionCreator<
  typeof ELEMENT_MOVED,
  ElementMovedPayload
> = createAction(ELEMENT_MOVED);

export const symbolRemoved: ActionCreator<
  typeof ROOT_SYMBOL_NODE_REMOVED,
  Component.ComponentName
> = createAction(ROOT_SYMBOL_NODE_REMOVED);
export const symbolRenamed: ActionCreator<
  typeof ROOT_SYMBOL_NODE_RENAMED,
  {
    id: string;
    newName: string;
    newDescription: string;
    newCategory: null | string;
    newGroupName: string;
    newPreviewImage: null | {
      url: string;
      id: string;
    };
    codeComponentSource?: {code: string; expectedSource: string};
  }
> = createAction(ROOT_SYMBOL_NODE_RENAMED);

export const symbolVisibilityChanged: ActionCreator<
  typeof ROOT_SYMBOL_NODE_VISIBILITY_CHANGED,
  {
    id: string;
    hiddenForNonComponentManagers: boolean;
  }
> = createAction(ROOT_SYMBOL_NODE_VISIBILITY_CHANGED);

export const selectedNodeRemoved: ActionCreator<
  typeof SELECTED_NODE_REMOVED,
  void
> = createAction(SELECTED_NODE_REMOVED);

export const imagePreviewUpdated: ActionCreator<
  typeof IMAGE_PREVIEW_UPDATED,
  {
    id: string;
    src: string;
  }
> = createAction(IMAGE_PREVIEW_UPDATED);

export type DataTypeChangedPayload = {
  id?: string;
  initialId: string;
  action: ExpressionEditorActionPayload;
};

export const dataTypeChanged: ActionCreator<
  typeof DATA_TYPE_CHANGED,
  DataTypeChangedPayload
> = createAction(DATA_TYPE_CHANGED);

export const dynamoBindingRemoved: ActionCreator<
  typeof DYNAMO_BINDING_REMOVED,
  {
    elementId: string;
    exprPath: ExpressionPath;
    componentId: string | null;
    initialId: string;
  }
> = createAction(DYNAMO_BINDING_REMOVED);

export const __HACK__updateClearfixState = createAction<
  typeof __HACK__CLEARFIX_STATE_CHANGED,
  Array<string>
>(__HACK__CLEARFIX_STATE_CHANGED);

export const dynBindingFromPageNodeRemoved: ActionCreator<
  typeof PAGE_NODE_DYN_BINDING_REMOVED,
  {
    bindingType: NodeBindingType;
    nodeId: string;
    operatorName: string | undefined;
    slug: string;
    usageCount: number;
    usageId: string;
    userHasRemovedBindings: boolean;
    value: unknown;
    propertyFieldId?: string;
    filterIndex?: number;
    filterExpression?: Expression;
  }
> = createAction(PAGE_NODE_DYN_BINDING_REMOVED);

export const dynBindingFromSymbolNodeRemoved: ActionCreator<
  typeof SYMBOL_NODE_DYN_BINDING_REMOVED,
  {
    bindingType: NodeBindingType;
    nodeId: string;
    operatorName: string | undefined;
    slug: string;
    usageCount: number;
    usageId: string;
    userHasRemovedBindings: boolean;
    value: unknown;
    propertyFieldId: string | undefined;
    filterIndex?: number;
    filterExpression?: Expression;
  }
> = createAction(SYMBOL_NODE_DYN_BINDING_REMOVED);
